"use client"

import React, { useRef, useMemo } from "react"
import { motion, useScroll, useTransform, useReducedMotion } from "motion/react"
import Image from "next/image"
import { CMSLink } from "@/components/Link"
import type { Media } from "@/payload-types"
import { cn } from "@/utilities/ui"

// Define the interface locally since it's not in payload-types.ts yet
export interface EnhancedCTABlock {
	title: string
	description?: string | null
	buttons?:
		| {
				label: string
				link: string
				style?: "primary" | "secondary" | "outline" | "ghost" | null
				size?: "sm" | "md" | "lg" | null
				id?: string | null
		  }[]
		| null
	background?: {
		type?: "solid" | "gradient" | "image" | null
		color?: string | null
		image?: string | Media | null
		overlayOpacity?: number | null
	} | null
	animation?: "none" | "fade-up" | "fade-in" | null
	id?: string | null
	blockName?: string | null
	blockType: "enhancedCTA"
}

export const EnhancedCTA: React.FC<EnhancedCTABlock> = ({
	title,
	description,
	buttons,
	background,
	animation = "fade-up",
}) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const shouldReduceMotion = useReducedMotion()

	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start end", "end start"],
	})

	const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"])

	const isDarkBackground = useMemo(() => {
		if (!background) return false
		if (background.type === "image" || background.type === "gradient")
			return true
		if (background.type === "solid") {
			const color = background.color?.toLowerCase() || ""
			// Default to true for primary/secondary colors or if empty (defaults to primary)
			if (
				!color ||
				color.includes("primary") ||
				color.includes("secondary") ||
				color.includes("black")
			)
				return true

			// Basic hex brightness check
			if (color.startsWith("#")) {
				const hex = color.replace("#", "")
				if (hex.length === 3 || hex.length === 6) {
					const r = parseInt(
						hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2),
						16,
					)
					const g = parseInt(
						hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4),
						16,
					)
					const b = parseInt(
						hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6),
						16,
					)
					// YIQ formula for brightness
					const brightness = (r * 299 + g * 587 + b * 114) / 1000
					return brightness < 128
				}
			}
		}
		return false
	}, [background])

	const renderBackground = () => {
		if (!background) return null

		const { type, color, image, overlayOpacity = 50 } = background

		if (type === "solid") {
			return (
				<div
					className="absolute inset-0 -z-10"
					style={{ backgroundColor: color || "var(--primary)" }}
				/>
			)
		}

		if (type === "gradient") {
			return (
				<div
					className="absolute inset-0 -z-10"
					style={{
						background:
							color ||
							"linear-gradient(to right, var(--primary), var(--secondary))",
					}}
				/>
			)
		}

		if (type === "image" && image) {
			const imageUrl = typeof image === "object" ? image.url : null
			const imageAlt = typeof image === "object" ? image.alt : ""

			if (!imageUrl) return null

			return (
				<div className="absolute inset-0 -z-10 overflow-hidden">
					<motion.div
						style={{ y: shouldReduceMotion ? 0 : y }}
						className="absolute inset-0 h-[140%] -top-[20%]"
					>
						<Image
							src={imageUrl}
							alt={imageAlt || ""}
							fill
							sizes="100vw"
							className="object-cover"
							priority={false}
						/>
					</motion.div>
					<div
						className="absolute inset-0 bg-black"
						style={{ opacity: (overlayOpacity || 0) / 100 }}
					/>
				</div>
			)
		}

		return null
	}

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.15,
				delayChildren: 0.1,
			},
		},
	}

	const itemVariants = {
		hidden: {
			opacity: 0,
			y:
				shouldReduceMotion || animation === "none"
					? 0
					: animation === "fade-up"
						? 30
						: 0,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.8,
				ease: [0.21, 0.47, 0.32, 0.98] as [number, number, number, number],
			},
		},
	}

	return (
		<section
			ref={containerRef}
			className="relative py-20 md:py-32 overflow-hidden"
		>
			{renderBackground()}

			<div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-100px" }}
					variants={containerVariants}
					className="max-w-3xl mx-auto text-center"
				>
					<motion.h2
						variants={itemVariants}
						className={cn(
							"text-3xl md:text-5xl font-bold mb-6 tracking-tight",
							isDarkBackground ? "text-white" : "text-foreground",
						)}
					>
						{title}
					</motion.h2>

					{description && (
						<motion.p
							variants={itemVariants}
							className={cn(
								"text-lg md:text-xl mb-10 opacity-90 leading-relaxed",
								isDarkBackground ? "text-white" : "text-muted-foreground",
							)}
						>
							{description}
						</motion.p>
					)}

					{buttons && buttons.length > 0 && (
						<div className="flex flex-wrap justify-center gap-4">
							{buttons.map((button, i) => {
								const { label, link, style, size, id } = button

								// Safe mapping for appearance
								let appearance:
									| "default"
									| "outline"
									| "secondary"
									| "ghost"
									| "link" = "default"
								if (style === "primary") appearance = "default"
								if (style === "secondary") appearance = "secondary"
								if (style === "outline") appearance = "outline"
								if (style === "ghost") appearance = "ghost"

								// Safe mapping for size
								let buttonSize: "default" | "sm" | "lg" | "icon" = "default"
								if (size === "sm") buttonSize = "sm"
								if (size === "md") buttonSize = "default"
								if (size === "lg") buttonSize = "lg"

								return (
									<motion.div
										key={id || i}
										variants={itemVariants}
										whileHover={
											shouldReduceMotion ? {} : { scale: 1.05, y: -2 }
										}
										whileTap={shouldReduceMotion ? {} : { scale: 0.98 }}
									>
										<CMSLink
											type="custom"
											url={link}
											label={label}
											appearance={appearance}
											size={buttonSize}
										/>
									</motion.div>
								)
							})}
						</div>
					)}
				</motion.div>
			</div>
		</section>
	)
}
